<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DashboardStatsAPI from 'dashboard/api/dashboardStats';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useAccount } from 'dashboard/composables/useAccount';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const stats = ref({
  contacts_count: 0,
  users_count: 0,
  lifecycle_stages: [],
  teams: [],
  team_members: [],
  conversations: {
    counts: { open: 0, assigned: 0, unassigned: 0 },
    open: [],
    assigned: [],
    unassigned: [],
  },
});
const isFetching = ref(false);
const lastUpdatedAt = ref(null);
const selectedConversationTab = ref('open');
const selectedTeamId = ref('all');
const selectedStatus = ref('all');

const lifecycleStages = computed(() => stats.value.lifecycle_stages || []);
const teams = computed(() => stats.value.teams || []);
const teamMembers = computed(() => stats.value.team_members || []);
const conversationStats = computed(
  () =>
    stats.value.conversations || {
      counts: { open: 0, assigned: 0, unassigned: 0 },
      open: [],
      assigned: [],
      unassigned: [],
    }
);

const statusOptions = computed(() => [
  { value: 'all', label: t('OWNER_DASHBOARD.TEAM_MEMBERS.ALL_STATUSES') },
  { value: 'online', label: t('OWNER_DASHBOARD.TEAM_MEMBERS.ONLINE') },
  { value: 'busy', label: t('OWNER_DASHBOARD.TEAM_MEMBERS.BUSY') },
  { value: 'offline', label: t('OWNER_DASHBOARD.TEAM_MEMBERS.OFFLINE') },
]);

const conversationTabs = computed(() => [
  {
    key: 'open',
    label: t('OWNER_DASHBOARD.CONTACTS.OPEN'),
    count: conversationStats.value.counts.open,
  },
  {
    key: 'assigned',
    label: t('OWNER_DASHBOARD.CONTACTS.ASSIGNED'),
    count: conversationStats.value.counts.assigned,
  },
  {
    key: 'unassigned',
    label: t('OWNER_DASHBOARD.CONTACTS.UNASSIGNED'),
    count: conversationStats.value.counts.unassigned,
  },
]);

const activeConversations = computed(
  () => conversationStats.value[selectedConversationTab.value] || []
);

const filteredTeamMembers = computed(() => {
  return teamMembers.value.filter(member => {
    const matchesTeam =
      selectedTeamId.value === 'all' ||
      member.teams?.some(team => String(team.id) === selectedTeamId.value);
    const matchesStatus =
      selectedStatus.value === 'all' ||
      member.availability_status === selectedStatus.value;

    return matchesTeam && matchesStatus;
  });
});

const stageMetrics = stage => [
  {
    key: 'open',
    label: t('OWNER_DASHBOARD.LIFECYCLE.OPEN_CONVERSATIONS'),
    value: stage.open_conversations_count,
    icon: 'i-lucide-message-circle',
  },
  {
    key: 'snoozed',
    label: t('OWNER_DASHBOARD.LIFECYCLE.SNOOZED_CONVERSATIONS'),
    value: stage.snoozed_conversations_count,
    icon: 'i-lucide-clock',
  },
  {
    key: 'unassigned',
    label: t('OWNER_DASHBOARD.LIFECYCLE.UNASSIGNED_CONVERSATIONS'),
    value: stage.unassigned_conversations_count,
    icon: 'i-lucide-user-x',
  },
];

const fetchStats = async () => {
  isFetching.value = true;
  try {
    const response = await DashboardStatsAPI.get();
    stats.value = response.data;
    lastUpdatedAt.value = new Date();
  } finally {
    isFetching.value = false;
  }
};

const formatPercentage = percentage => `${Number(percentage || 0).toFixed(2)}%`;

const formatCount = count => (Number(count || 0) > 0 ? count : '–');

const shortActivityTime = timestamp =>
  timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';

const initials = name =>
  (name || '?')
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const statusDotClass = status => {
  const statusClassMap = {
    online: 'bg-n-teal-9',
    busy: 'bg-n-amber-9',
    offline: 'bg-n-slate-8',
  };

  return statusClassMap[status] || statusClassMap.offline;
};

onMounted(fetchStats);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <header
      class="flex items-center justify-between gap-3 px-4 py-3 border-b border-n-weak"
    >
      <h1 class="m-0 text-base font-semibold text-n-slate-12">
        {{ t('OWNER_DASHBOARD.TITLE') }}
      </h1>
      <div class="flex items-center gap-3">
        <p v-if="lastUpdatedAt" class="m-0 text-xs text-right text-n-slate-10">
          {{ t('OWNER_DASHBOARD.LAST_UPDATED') }}
          {{ t('OWNER_DASHBOARD.JUST_NOW') }}
        </p>
        <button
          type="button"
          class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-n-solid-1 bg-n-brand hover:bg-n-brand/90 disabled:opacity-60"
          :disabled="isFetching"
          @click="fetchStats"
        >
          <span
            class="i-lucide-refresh-cw size-4"
            :class="{ 'animate-spin': isFetching }"
          />
          <span>{{ t('OWNER_DASHBOARD.REFRESH') }}</span>
        </button>
      </div>
    </header>

    <div class="grid gap-6 p-6">
      <section class="grid gap-4">
        <h2 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ t('OWNER_DASHBOARD.LIFECYCLE.TITLE') }}
        </h2>

        <div
          v-if="lifecycleStages.length"
          class="flex gap-4 pb-2 overflow-x-auto"
        >
          <article
            v-for="stage in lifecycleStages"
            :key="stage.id"
            class="grid flex-none w-64 gap-4 p-4 border rounded-lg bg-n-solid-2 border-n-weak"
          >
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-xl">{{ stage.icon }}</span>
              <h3 class="m-0 text-sm font-semibold truncate text-n-slate-12">
                {{ stage.name }}
              </h3>
            </div>

            <div class="flex items-end gap-2">
              <span class="text-3xl font-semibold text-n-slate-12">
                {{ stage.contacts_count }}
              </span>
              <span class="pb-1 text-sm font-medium text-n-teal-11">
                {{ formatPercentage(stage.percentage) }}
              </span>
            </div>

            <div class="flex items-center gap-4">
              <button
                v-for="metric in stageMetrics(stage)"
                :key="metric.key"
                v-tooltip.top="metric.label"
                type="button"
                class="inline-flex items-center gap-1.5 text-sm text-n-slate-10"
              >
                <span class="size-4" :class="metric.icon" />
                <span class="font-semibold text-n-slate-11">
                  {{ metric.value }}
                </span>
              </button>
            </div>
          </article>
        </div>

        <p v-else class="m-0 text-sm text-n-slate-10">
          {{ t('OWNER_DASHBOARD.LIFECYCLE.NO_STAGES') }}
        </p>
      </section>

      <section class="grid gap-4 xl:grid-cols-2">
        <article
          class="grid min-h-[28rem] border rounded-lg bg-n-solid-2 border-n-weak"
        >
          <div class="p-5">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('OWNER_DASHBOARD.CONTACTS.TITLE') }}
            </h2>

            <div class="flex items-center gap-6 mt-6 border-b border-n-weak">
              <button
                v-for="tab in conversationTabs"
                :key="tab.key"
                type="button"
                class="pb-3 text-sm font-semibold border-b-2 transition-colors"
                :class="
                  selectedConversationTab === tab.key
                    ? 'text-n-brand border-n-brand'
                    : 'text-n-slate-10 border-transparent hover:text-n-slate-12'
                "
                @click="selectedConversationTab = tab.key"
              >
                {{ tab.label }}
                <span class="ms-1">{{ formatCount(tab.count) }}</span>
              </button>
            </div>

            <div v-if="activeConversations.length" class="grid gap-4 mt-8">
              <RouterLink
                v-for="conversation in activeConversations"
                :key="conversation.id"
                :to="
                  accountScopedRoute('inbox_conversation', {
                    conversation_id: conversation.display_id,
                  })
                "
                class="flex items-center justify-between gap-4 p-2 -mx-2 rounded-lg hover:bg-n-alpha-2"
              >
                <div class="flex items-center gap-3 min-w-0">
                  <img
                    v-if="conversation.contact.thumbnail"
                    :src="conversation.contact.thumbnail"
                    :alt="conversation.contact.name"
                    class="object-cover rounded-full size-10"
                  />
                  <span
                    v-else
                    class="inline-flex items-center justify-center font-semibold rounded-full size-10 text-n-slate-12 bg-n-alpha-2"
                  >
                    {{ initials(conversation.contact.name) }}
                  </span>
                  <div class="min-w-0">
                    <p
                      class="m-0 text-sm font-semibold truncate text-n-slate-12"
                    >
                      {{ conversation.contact.name }}
                    </p>
                    <p class="m-0 text-xs truncate text-n-slate-10">
                      {{
                        t('OWNER_DASHBOARD.CONTACTS.CONVERSATION_ID', {
                          id: conversation.display_id,
                        })
                      }}
                      <span v-if="conversation.inbox.name" class="ms-1">
                        {{ conversation.inbox.name }}
                      </span>
                    </p>
                  </div>
                </div>

                <div class="flex items-center gap-3 text-right min-w-0">
                  <div class="min-w-0">
                    <p class="m-0 text-xs text-n-slate-10">
                      {{ shortActivityTime(conversation.last_activity_at) }}
                    </p>
                    <p class="m-0 text-sm truncate text-n-slate-12">
                      {{
                        conversation.assignee.name ||
                        t('OWNER_DASHBOARD.CONTACTS.UNASSIGNED')
                      }}
                    </p>
                  </div>
                  <img
                    v-if="conversation.assignee.thumbnail"
                    :src="conversation.assignee.thumbnail"
                    :alt="conversation.assignee.name"
                    class="object-cover rounded-full size-9"
                  />
                  <span
                    v-else
                    class="inline-flex items-center justify-center text-xs font-semibold rounded-full size-9 text-n-slate-12 bg-n-alpha-2"
                  >
                    {{ initials(conversation.assignee.name) }}
                  </span>
                </div>
              </RouterLink>
            </div>

            <p v-else class="mt-16 text-sm text-center text-n-slate-10">
              {{ t('OWNER_DASHBOARD.CONTACTS.EMPTY') }}
            </p>
          </div>

          <footer
            class="flex items-center justify-between self-end px-5 py-4 border-t border-n-weak"
          >
            <p class="m-0 text-sm text-n-slate-10">
              {{ t('OWNER_DASHBOARD.PAGE_COUNT') }}
            </p>
            <RouterLink
              :to="accountScopedRoute('conversations_index')"
              class="px-3 py-2 text-sm font-medium border rounded-lg text-n-slate-12 border-n-weak hover:bg-n-alpha-2"
            >
              {{ t('OWNER_DASHBOARD.CONTACTS.VIEW_ALL') }}
            </RouterLink>
          </footer>
        </article>

        <article
          class="grid min-h-[28rem] border rounded-lg bg-n-solid-2 border-n-weak"
        >
          <div class="p-5">
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('OWNER_DASHBOARD.TEAM_MEMBERS.TITLE') }}
            </h2>

            <div class="grid gap-3 mt-4 md:grid-cols-2">
              <label class="relative block">
                <select
                  v-model="selectedTeamId"
                  class="reset-base w-full h-10 py-0 pl-3 pr-10 text-sm border rounded-lg appearance-none outline-none text-n-slate-11 bg-n-solid-1 border-n-strong hover:border-n-slate-8 focus:border-n-brand"
                >
                  <option value="all">
                    {{ t('OWNER_DASHBOARD.TEAM_MEMBERS.ALL_TEAMS') }}
                  </option>
                  <option
                    v-for="team in teams"
                    :key="team.id"
                    :value="String(team.id)"
                  >
                    {{ team.name }}
                  </option>
                </select>
                <span
                  class="absolute -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 right-3 top-1/2 text-n-slate-10"
                />
              </label>
              <label class="relative block">
                <select
                  v-model="selectedStatus"
                  class="reset-base w-full h-10 py-0 pl-3 pr-10 text-sm border rounded-lg appearance-none outline-none text-n-slate-11 bg-n-solid-1 border-n-strong hover:border-n-slate-8 focus:border-n-brand"
                >
                  <option
                    v-for="status in statusOptions"
                    :key="status.value"
                    :value="status.value"
                  >
                    {{ status.label }}
                  </option>
                </select>
                <span
                  class="absolute -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 right-3 top-1/2 text-n-slate-10"
                />
              </label>
            </div>

            <div v-if="filteredTeamMembers.length" class="grid mt-8">
              <div
                v-for="member in filteredTeamMembers"
                :key="member.id"
                class="flex items-start justify-between gap-4 py-4 border-b border-n-weak last:border-b-0"
              >
                <div class="flex items-start gap-3 min-w-0">
                  <span class="relative flex-none">
                    <img
                      v-if="member.thumbnail"
                      :src="member.thumbnail"
                      :alt="member.name"
                      class="object-cover rounded-full size-10"
                    />
                    <span
                      v-else
                      class="inline-flex items-center justify-center font-semibold rounded-full size-10 text-n-slate-12 bg-n-alpha-2"
                    >
                      {{ initials(member.name) }}
                    </span>
                    <span
                      class="absolute bottom-0 right-0 rounded-full size-2.5 ring-2 ring-n-solid-2"
                      :class="statusDotClass(member.availability_status)"
                    />
                  </span>
                  <div class="min-w-0">
                    <p
                      class="m-0 text-sm font-semibold truncate text-n-slate-12"
                    >
                      {{ member.name }}
                    </p>
                    <p class="m-0 mt-1 text-xs text-n-slate-10">
                      {{
                        t('OWNER_DASHBOARD.TEAM_MEMBERS.ASSIGNED_CONTACTS', {
                          count: member.assigned_contacts_count,
                        })
                      }}
                    </p>
                  </div>
                </div>

                <div class="grid gap-1 text-right text-n-slate-10">
                  <span class="text-xs">
                    {{ t('OWNER_DASHBOARD.TEAM_MEMBERS.SINCE') }}
                    {{ t('OWNER_DASHBOARD.JUST_NOW') }}
                  </span>
                  <span
                    v-for="team in member.teams"
                    :key="team.id"
                    class="inline-flex items-center justify-end gap-1 text-sm text-n-slate-12"
                  >
                    <span class="i-lucide-users size-4" />
                    {{ team.name }}
                  </span>
                </div>
              </div>
            </div>

            <p v-else class="mt-16 text-sm text-center text-n-slate-10">
              {{ t('OWNER_DASHBOARD.TEAM_MEMBERS.EMPTY') }}
            </p>
          </div>

          <footer
            class="flex items-center justify-between self-end px-5 py-4 border-t border-n-weak"
          >
            <p class="m-0 text-sm text-n-slate-10">
              {{ t('OWNER_DASHBOARD.PAGE_COUNT') }}
            </p>
            <RouterLink
              :to="accountScopedRoute('agent_list')"
              class="px-3 py-2 text-sm font-medium border rounded-lg text-n-slate-12 border-n-weak hover:bg-n-alpha-2"
            >
              {{ t('OWNER_DASHBOARD.TEAM_MEMBERS.VIEW_ALL') }}
            </RouterLink>
          </footer>
        </article>
      </section>
    </div>
  </section>
</template>
